import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import type { Ref } from 'vue';
import { computed, onUnmounted, ref } from 'vue';
import { deepCompareComputed } from '@/composable/deepCompareComputed';
import { isInvalidLiveMp1X2Odds, isValidOdds } from '@/core/lib/oddsHelper';
import { deepFreeze } from '@/core/lib/utils';
import { useOddsToken } from '@/core/oddsApi/composable/useOddsToken';
import { formatOdds } from '@/core/oddsApi/helpers/formatOdds';
import {
    EnumOddsCategoryType,
    useOddsSubscription,
} from '@/core/oddsApi/oddsApiType';
import type {
    EnumPresetFilterType,
} from '@/core/oddsApi/oddsApiType';
import type { IOdds } from '@/interface/IOdds';
import type { LeagueSize, SportType } from '@/interface/enum';
import { PriceStyle } from '@/interface/enum';

export function useOddsSubscriptionCallback({
    eventId,
    oddsId,
    presetFilter,
    oddsCategory,
    priceStyle,
    isActive = ref(true),
    sportType,
    leagueSize,
}: {
    eventId: number;
    oddsId?: number;
    presetFilter: EnumPresetFilterType.Live | EnumPresetFilterType.NonLive;
    oddsCategory: EnumOddsCategoryType;
    priceStyle: MaybeRef<PriceStyle>;
    isActive?: Ref<boolean>;
    sportType: SportType;
    leagueSize: LeagueSize;
}) {
    const { getOddsToken } = useOddsToken();

    const variables = computed(() => {
        const oddsToken = getOddsToken(sportType, leagueSize);

        return {
            query: {
                id: eventId,
                oddsId,
                filter: presetFilter,
                oddsCategory,
                priceStyle: PriceStyle.toOddsApi(get(priceStyle)),
                oddsToken,
            },
        };
    });

    const { onSubscription } = useOddsSubscription(
        variables,
        deepCompareComputed(() => ({
            enabled: isActive.value && !!variables.value.query.oddsToken,
        })),
    );

    type UpdatedFn = (odds: IOdds) => void;
    let updatedFn: UpdatedFn[] = [];
    const onUpdated = (callback: UpdatedFn) => {
        updatedFn.push(callback);
        onUnmounted(() => {
            updatedFn = updatedFn.filter(fn => fn !== callback);
        });
    };

    type DeletedFn = (oddsId: number) => void;
    let deletedFn: DeletedFn[] = [];
    const onDeleted = (callback: DeletedFn) => {
        deletedFn.push(callback);
        onUnmounted(() => {
            deletedFn = deletedFn.filter(fn => fn !== callback);
        });
    };

    onSubscription((subscription) => {
        subscription?.updated.forEach((toUpdate) => {
            if (toUpdate.id === oddsId) {
                const updatedOdds = deepFreeze(formatOdds(toUpdate));
                const isMixParlayMode = oddsCategory === EnumOddsCategoryType.MixParlay;
                if (isValidOdds(updatedOdds) && !isInvalidLiveMp1X2Odds(updatedOdds, isMixParlayMode)) {
                    updatedFn.forEach(fn => fn(updatedOdds));
                } else {
                    deletedFn.forEach(fn => fn(updatedOdds.id));
                }
            }
        });

        subscription?.deleted.forEach((toDeleteId) => {
            if (toDeleteId === oddsId) {
                deletedFn.forEach(fn => fn(toDeleteId));
            }
        });
    });

    return {
        onUpdated,
        onDeleted,
    };
}
